import { Input, InputProps, Tag } from "@p0security/ui";
import classNames from "classnames";
import { pullAt, uniq } from "lodash";
import React, { useMemo, useState } from "react";

/** An Input where the prefix automatically wraps as input overflows
 *
 * Begins as a single-line input with prefix items prepended to the input;
 * automatically forms two lines once the available input length falls below 15em.
 */
const WRAPPED_INPUT_CLASSES =
  "flex-row flex-wrap [&_.ant-input]:mb-0! [&_.ant-input]:flex-[1_1_15em]! [&_.ant-input-prefix]:mr-0! [&_.ant-input-prefix]:max-w-full! [&_.ant-input-prefix]:flex-row! [&_.ant-input-prefix]:flex-wrap! [&_.ant-input-prefix]:gap-x-0! [&_.ant-input-prefix]:gap-y-[0.5em]!";

type TagInputProps = InputProps & {
  tags: string[];
  onUpdate: (tags: string[]) => void;
  tagToElement?: (tag: string, onClose: () => void) => JSX.Element;
  /** Character class or fixed strings to match for tags */
  tagPattern?: RegExp;
};

const defaultTagToElement = (tag: string, onClose: () => void) => {
  return (
    <Tag key={tag} closable={true} onClose={onClose}>
      {tag}
    </Tag>
  );
};

const defaultTagPattern = /[^\s,;]/;

export const TagInput: React.FC<TagInputProps> = (props) => {
  const {
    tags,
    tagPattern,
    tagToElement,
    onUpdate,
    disabled,
    placeholder,
    ...inputProps
  } = props;
  const [text, setText] = useState("");
  const updateTags = useMemo(
    () => (newTags: string[]) => onUpdate(uniq(newTags.sort())),
    [onUpdate]
  );
  const tagClass = tagPattern ?? defaultTagPattern;
  const [splitRegex, matchRegex] = useMemo(
    () => [
      new RegExp(`.+(?<!${tagClass.source})`),
      new RegExp(`(?:${tagClass.source})+`),
    ],
    [tagClass.source]
  );
  const tagElements = useMemo(
    () =>
      tags.map((v, ix) => {
        const onTagClose = () => {
          // have to clone tags array otherwise diffing does not work
          const newTags = [...tags];
          pullAt(newTags, ix);
          return updateTags(newTags);
        };
        return (tagToElement ?? defaultTagToElement)(v, onTagClose);
      }),
    [tagToElement, tags, updateTags]
  );
  const inputOnChange = useMemo(
    () =>
      (event: React.SyntheticEvent<HTMLInputElement> & { key?: string }) => {
        const soFar = event.currentTarget.value;
        if (event.key === "Enter" || soFar.match(splitRegex)) {
          const newItem = soFar.match(matchRegex);
          if (!newItem) setText(soFar);
          else {
            updateTags([...tags, newItem[0]]);
            setText("");
          }
        } else {
          setText(soFar);
        }
      },
    [matchRegex, splitRegex, tags, updateTags]
  );
  return (
    <Input
      {...inputProps}
      className={classNames(WRAPPED_INPUT_CLASSES, inputProps.className)}
      prefix={<>{tagElements}</>}
      onChange={inputOnChange}
      onPressEnter={inputOnChange}
      value={text}
      disabled={disabled}
      placeholder={placeholder}
    />
  );
};
